import { createApi } from '@reduxjs/toolkit/query/react';
import { endPoints, urlKeys } from 'src/api/APIEndpoints';
import { archLevel } from 'src/data/config';
import { ISIVReport } from 'src/types/SIVReport';
import { HTTPValidationError, ISiteUpdate, ISuccessfulResponse } from 'src/types/SiteStudyPersonal';
import { formatString } from 'src/util/STR';
import { baseQuery } from './baseQuery';

export const sivReportApi = createApi({
  baseQuery,
  reducerPath: 'sivReportApi',
  endpoints: builder => ({
    getSivReport: builder.query<
      ISIVReport,
      {
        monitoring_visit_id: string | null;
        site_id: string | null;
        study_id: string | null;
        country: string | null;
        application: string | undefined;
      }
    >({
      query: ({ monitoring_visit_id, site_id, study_id, country, application }) => ({
        url: formatString(
          endPoints.SivReport.select.url,
          //archLevel == 'site' ? endPoints.SivReport.getAll.url : endPoints.SivReport.select.url,
          { key: urlKeys.monitoring_visit_id, value: monitoring_visit_id },
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application }
        )
      }),
      extraOptions: { maxRetries: 3 },
      keepUnusedDataFor: endPoints.SivChecklist.select.keepUnusedDataFor
    }),

    updateSivChecklist: builder.mutation<ISuccessfulResponse | HTTPValidationError, ISiteUpdate>({
      query: ({ site_id, study_id, country, application, data }) => ({
        url: formatString(
          endPoints.Site.update,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application }
        ),
        method: 'PUT',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    signSivReport: builder.mutation<any, any>({
      query: ({ site_id, study_id, country, application, data }) => ({
        url: `${formatString(
          endPoints.SivReport.signSivReport,
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.site_id, value: site_id }
        )}?country=${country}&application=${application}`,
        method: 'PUT',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getAllSIVReports: builder.mutation<any, any>({
      query: ({ monitoring_visit_id, site_id, study_id, country, application }) => ({
        url: `${formatString(
          endPoints.SivReport.getAllSivReports
        )}?study_id=${study_id}&site_id=${site_id}&country=${country}&application=${
          archLevel == 'site' ? 'TSDA' : application
        }`
      }),

      extraOptions: { maxRetries: 3 }
    }),

    publishSIVReportToEtmf: builder.mutation<any, any>({
      query: ({
        site_id,
        study_id,
        country,
        publish_doc_version,
        is_published,
        monitoring_visit_id,
        update,
        application,
        application_section
      }) => ({
        url: `${formatString(
          endPoints.SivReport.pulishEtmf,
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.site_id, value: site_id }
        )}?country=${country}&publish_doc_version=${publish_doc_version}&monitoring_visit_id=${monitoring_visit_id}&is_published=${is_published}&update=${update}&application=${application}&application_section=${application_section}`,
        method: 'POST'
      })
    }),

    sendForReview: builder.mutation<any, any>({
      query: ({ site_id, study_id, country, data, application, monitoring_visit_id }) => ({
        url: `${formatString(
          endPoints.SivReport.notifyUsers,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}&monitoring_visit_id=${monitoring_visit_id}&application_section=CONDUCT&application_module_section=IMV%20REPORT`,
        method: 'PUT',
        body: data
      })
    })
  })
});

export const {
  useGetSivReportQuery,
  useSignSivReportMutation,
  useGetAllSIVReportsMutation,
  usePublishSIVReportToEtmfMutation,
  useSendForReviewMutation
} = sivReportApi;
